import { Notifier, type Hub, type KeyValue, type Notice } from "@foreman/app-core";
import { alertWanted, type AlertPrefs } from "@foreman/protocol";
import { OPEN_ALERT_EVENT, type AlertTarget } from "./alert-open";

/** What the Inbox screen shows about alerts. `denied` and `unsupported` can't be fixed from inside the app. */
export type AlertState = "unsupported" | "off" | "on" | "denied";

const PREF = "foreman.alerts";
/** Alerts name the session (agent, folder, chat name) unless this is "0". Off: fixed wording, nothing about a session leaves the computer. */
const DETAILS_PREF = "foreman.alerts.details";
/** "0": no alert when an agent finishes. */
const FINISHED_PREF = "foreman.alerts.finished";
/** Quiet hours, as JSON `{ from, to }` (minutes after midnight in this phone's time zone); absent: off. */
const QUIET_PREF = "foreman.alerts.quiet";
const DEFAULT_QUIET = { from: 22 * 60, to: 7 * 60 };
const zone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

/** The Android app (a Capacitor WebView) has no Web Push: it registers a Firebase token through the native plugin instead. */
const nativePush = (): any => {
  const cap = (window as any).Capacitor;
  return cap?.isNativePlatform?.() ? cap.Plugins?.PushNotifications ?? null : null;
};
export const OPEN_INBOX_EVENT = "foreman:open-inbox";
/** A tapped alert: the app goes to the card or session it was about. */
const openTarget = (target: AlertTarget) => window.dispatchEvent(new CustomEvent(OPEN_ALERT_EVENT, { detail: target }));

/**
 * Alerts for new inbox cards, using the browser's own notifications. While the app is open (in another tab or the
 * background) they come from the page itself. When it is closed, the computer asks the relay to send a Web Push
 * (an empty one: the service worker shows a fixed "needs you" alert, so nothing about a session ever leaves the
 * computer except through the encrypted channel). The rules (what is news, what clears) live in app-core's
 * Notifier; this decides how to show it in a browser and keeps the computers told where to wake this phone.
 */
export class WebNotifications {
  private notifier: (() => void) | null = null;
  private readonly open = new Map<string, Notification>();
  private readonly listeners = new Set<() => void>();
  private cached: AlertState;
  private nativePerm: "granted" | "denied" | "prompt" | null = null;

  constructor(
    private readonly hub: Hub,
    private readonly kv: KeyValue,
  ) {
    this.cached = this.compute();
  }

  private compute(): AlertState {
    if (nativePush()) {
      if (this.nativePerm === "denied") return "denied";
      return this.nativePerm === "granted" && this.kv.get(PREF) === "1" ? "on" : "off";
    }
    if (typeof Notification === "undefined") return "unsupported";
    if (Notification.permission === "denied") return "denied";
    return Notification.permission === "granted" && this.kv.get(PREF) === "1" ? "on" : "off";
  }

  subscribe = (cb: () => void): (() => void) => {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  };
  getState = (): AlertState => this.cached;

  private refresh(): void {
    const next = this.compute();
    if (next === this.cached) return;
    this.cached = next;
    for (const l of [...this.listeners]) l();
  }

  start(): void {
    this.refresh();
    const native = nativePush();
    if (native) {
      void native.checkPermissions().then(
        (p: { receive: string }) => {
          this.nativePerm = p.receive === "granted" ? "granted" : p.receive === "denied" ? "denied" : "prompt";
          this.refresh();
          if (this.cached === "on") this.run();
        },
        () => {},
      );
      return;
    }
    if (this.cached === "on") this.run();
    // a tap on a push alert while the app is already open
    navigator.serviceWorker?.addEventListener("message", (e: MessageEvent) => {
      if (e.data?.type === "open-alert" && typeof e.data.eventId === "string") openTarget({ eventId: e.data.eventId, sessionId: e.data.sessionId, kind: e.data.kind });
      else if (e.data?.type === "open-inbox") window.dispatchEvent(new Event(OPEN_INBOX_EVENT));
      // Approve or Deny pressed on an alert: answered here, over the same encrypted channel as a tap on the card
      if (e.data?.type === "inbox-decide" && typeof e.data.eventId === "string" && (e.data.action === "approve" || e.data.action === "deny")) void this.decide(e.data.eventId, e.data.action);
    });
    // an alert button pressed while the app was closed left its answer with the service worker: ask for it
    navigator.serviceWorker?.ready.then((r) => r.active?.postMessage({ type: "sw-hello" })).catch(() => {});
  }

  /** Answer an approval from its alert. Waits (up to 20 s) for the card to arrive when the app was just opened. Only an open approval is ever answered. */
  private decide(eventId: string, action: "approve" | "deny"): Promise<void> {
    return new Promise((resolve) => {
      let off: (() => void) | null = null;
      let timer: ReturnType<typeof setTimeout> | undefined;
      const finish = () => {
        off?.();
        clearTimeout(timer);
        resolve();
      };
      const attempt = () => {
        const item = this.hub.getInbox().find((i) => i.event.id === eventId);
        if (!item) return false;
        if (item.event.state === "open" && item.event.kind === "approval") void this.hub.get(item.computer.id)?.respondInbox(eventId, action).catch(() => {});
        return true;
      };
      if (attempt()) return finish();
      off = this.hub.subscribe(() => attempt() && finish());
      timer = setTimeout(finish, 20_000);
    });
  }

  /** Tell me when an agent finishes. On unless the person turned it off. */
  getFinished = (): boolean => this.kv.get(FINISHED_PREF) !== "0";
  setFinished(on: boolean): void {
    if (on) this.kv.remove(FINISHED_PREF);
    else this.kv.set(FINISHED_PREF, "0");
    this.prefsChanged();
  }

  /** The saved quiet hours as stored (a stable value for React; "" when off). */
  getQuietRaw = (): string => this.kv.get(QUIET_PREF) ?? "";

  /** Quiet hours, or null when off. Only approvals and questions alert during them. */
  getQuiet = (): { from: number; to: number } | null => {
    try {
      const v = JSON.parse(this.kv.get(QUIET_PREF) ?? "null");
      return v && Number.isInteger(v.from) && Number.isInteger(v.to) ? { from: v.from, to: v.to } : null;
    } catch {
      return null;
    }
  };
  setQuiet(q: { from: number; to: number } | null): void {
    if (q) this.kv.set(QUIET_PREF, JSON.stringify(q));
    else this.kv.remove(QUIET_PREF);
    this.prefsChanged();
  }
  static defaultQuiet = DEFAULT_QUIET;

  /** What this phone wants to hear about, in the shape the computer and the in-page alerts both use. */
  prefs = (): AlertPrefs => {
    const quiet = this.getQuiet();
    return { ...(this.getFinished() ? {} : { finished: false }), ...(quiet ? { quiet: { ...quiet, tz: zone() } } : {}) };
  };

  private prefsChanged(): void {
    this.applied.clear();
    this.applyEndpoint(); // the computers decide which pushes wake this phone, so they must hear the change
    for (const l of [...this.listeners]) l();
  }

  /** Whether alerts may name the session. On unless the person turned it off. */
  getDetails = (): boolean => this.kv.get(DETAILS_PREF) !== "0";
  setDetails(on: boolean): void {
    if (on) this.kv.remove(DETAILS_PREF);
    else this.kv.set(DETAILS_PREF, "0");
    this.applied.clear();
    this.applyEndpoint(); // tell every computer which wording this phone wants
    for (const l of [...this.listeners]) l();
  }

  /** Ask the browser for permission (must be called from a tap) and turn alerts on. */
  async enable(): Promise<AlertState> {
    const native = nativePush();
    if (native) {
      try {
        const p = (await native.requestPermissions()) as { receive: string };
        this.nativePerm = p.receive === "granted" ? "granted" : p.receive === "denied" ? "denied" : "prompt";
      } catch {
        /* leave it as it was */
      }
      if (this.nativePerm === "granted") {
        this.kv.set(PREF, "1");
        this.run();
      }
      this.refresh();
      return this.cached;
    }
    if (typeof Notification === "undefined") return "unsupported";
    let permission = Notification.permission;
    if (permission === "default") {
      try {
        permission = await Notification.requestPermission();
      } catch {
        permission = Notification.permission;
      }
    }
    if (permission === "granted") {
      this.kv.set(PREF, "1");
      this.run();
    }
    this.refresh();
    return this.cached;
  }

  disable(): void {
    this.kv.remove(PREF);
    void this.pushOff();
    this.stop();
    this.refresh();
  }

  // ---- background alerts (Web Push) ------------------------------------------------------

  private applied = new Set<string>();
  private endpoint: string | null = null;
  private keys: { p256dh: string; auth: string } | undefined;
  private offHub: (() => void) | null = null;

  /** Subscribe this phone with the browser's push service and tell every computer where to wake it. Best effort. */
  private async pushOn(): Promise<void> {
    const native = nativePush();
    if (native) return this.pushOnNative(native);
    try {
      if (!("PushManager" in window)) return;
      const reg = await navigator.serviceWorker?.getRegistration();
      const first = this.hub.getSnapshot()[0]?.computer;
      if (!reg || !first) return;
      // The relay's public key comes from the relay itself (the address is in the pairing code), so the app needs no build-time link to it.
      const res = await fetch(`${first.relayUrl.replace(/^ws/, "http")}/vapid`);
      if (!res.ok) return; // e.g. a local relay: no push, the in-page alerts still work
      const { publicKey } = (await res.json()) as { publicKey: string | null };
      if (!publicKey) return;
      const key = Uint8Array.from(atob(publicKey.replace(/-/g, "+").replace(/_/g, "/")), (c) => c.charCodeAt(0));
      let sub = await reg.pushManager.getSubscription();
      const same = sub?.options.applicationServerKey && new Uint8Array(sub.options.applicationServerKey).every((b, i) => b === key[i]);
      if (sub && !same) {
        await sub.unsubscribe();
        sub = null;
      }
      sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
      this.endpoint = sub.endpoint;
      const k = sub.toJSON().keys;
      this.keys = k?.p256dh && k.auth ? { p256dh: k.p256dh, auth: k.auth } : undefined;
      this.applied.clear();
      this.applyEndpoint();
      this.offHub ??= this.hub.subscribe(() => this.applyEndpoint()); // computers paired later
    } catch {
      /* push is a bonus: any failure leaves the in-page alerts working */
    }
  }

  /** Android: ask Firebase for this phone's token and give every computer `fcm:<token>` as its address. */
  private async pushOnNative(native: any): Promise<void> {
    try {
      await native.addListener("registration", (t: { value: string }) => {
        this.endpoint = `fcm:${t.value}`;
        this.applied.clear();
        this.applyEndpoint();
        this.offHub ??= this.hub.subscribe(() => this.applyEndpoint()); // computers paired later
      });
      await native.addListener("pushNotificationActionPerformed", (a: { notification?: { data?: Record<string, unknown> } }) => {
        const d = a?.notification?.data;
        if (d && typeof d.eventId === "string") openTarget({ eventId: d.eventId, ...(typeof d.sessionId === "string" && { sessionId: d.sessionId }), ...(typeof d.kind === "string" && { kind: d.kind }) });
        else window.dispatchEvent(new Event(OPEN_INBOX_EVENT));
      });
      await native.register();
    } catch {
      /* push is a bonus: the in-app alerts still work */
    }
  }

  private applyEndpoint(): void {
    if (!this.endpoint) return;
    for (const { computer } of this.hub.getSnapshot()) {
      if (this.applied.has(computer.id)) continue;
      const conn = this.hub.get(computer.id);
      if (!conn) continue;
      conn.setPushEndpoint(this.endpoint, { ...(this.keys && { keys: this.keys }), ...(this.getDetails() && { details: true }), ...this.prefs() });
      this.applied.add(computer.id);
    }
  }

  private async pushOff(): Promise<void> {
    this.offHub?.();
    this.offHub = null;
    this.endpoint = null;
    this.keys = undefined;
    for (const { computer } of this.hub.getSnapshot()) this.hub.get(computer.id)?.setPushEndpoint(null);
    this.applied.clear();
    try {
      const native = nativePush();
      if (native) return void (await native.unregister?.());
      await (await navigator.serviceWorker?.getRegistration())?.pushManager.getSubscription().then((s) => s?.unsubscribe());
    } catch {
      /* nothing to undo */
    }
  }

  private run(): void {
    if (this.notifier) return;
    void this.pushOn();
    const notifier = new Notifier(this.hub, {
      shouldNotify: () => document.visibilityState === "hidden" || !document.hasFocus(),
      allow: (event) => alertWanted(this.prefs(), event.kind),
      multi: () => this.hub.getSnapshot().length > 1,
      notify: (n) => this.show(n),
      clear: (tag) => {
        this.open.get(tag)?.close();
        this.open.delete(tag);
      },
    });
    this.notifier = notifier.start();
  }

  private stop(): void {
    this.notifier?.();
    this.notifier = null;
    for (const n of this.open.values()) n.close();
    this.open.clear();
  }

  private show(n: Notice): void {
    try {
      const note = new Notification(n.title, { body: n.body, tag: n.tag });
      note.onclick = () => {
        window.focus();
        openTarget({ eventId: n.eventId, ...(n.sessionId && { sessionId: n.sessionId }), kind: n.kind });
        note.close();
      };
      note.onclose = () => this.open.delete(n.tag);
      this.open.set(n.tag, note);
    } catch {
      /* some mobile browsers only allow notifications through a service worker; nothing to show then */
    }
  }
}
